
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import { TableItemFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import moment from 'moment';
import { getTableName } from '@/worker/db/sqlHelpers';

@Component({
	components: {
		TableFilterable,
		SelectFilter,
	},
})
export default class SupplierUpcomingDueTable extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['suppliers'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
		| 'duePeriod'
	> = ['suppliers', 'agents', 'dateAsOf', 'duePeriod'];

	public period: '14 days' | '30 days' | '60 days' = '30 days';
	public periodOptions = ['14 days', '30 days', '60 days'];

	public tableItems: any[] = [];
	public tableFields: any[] = [];

	public get exportFileName() {
		const formattedDate = moment(this.selectedAsOfDate).format('DD MMM YY');
		return 'Supplier Upcoming Due' + '_' + 'As Of ' + formattedDate;
	}

	public get dateFormatted() {
		return [this.selectedAsOfDate];
	}

	public get selectedDate() {
		const periodArgument = this.period.split(' ');
		const day = Number(periodArgument[0]);
		return day;
	}

	public get expensiveHook() {
		const {
			selectedDate,
			selectedSuppliers,
			selectedAsOfDate,
			selectedAgents,
		} = this;
		return JSON.stringify([
			selectedDate,
			selectedSuppliers,
			selectedAsOfDate,
			selectedAgents,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.4);

		const msInDay = 24 * 60 * 60 * 1000;
		const dueDateStart = this.selectedAsOfDate;
		const dueDateEnd = this.selectedAsOfDate + this.selectedDate * msInDay;

		const queryHead = await this._ensureTablesLoaded(
			['supplierPiSds'] as any[],
		);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.4, 0.9);

		const piSdsTable = getTableName('supplierPiSds');
		const dateFilter = `TRY_CAST("DUEDATE" AS BIGINT) >= ${dueDateStart}`
			+ ` AND TRY_CAST("DUEDATE" AS BIGINT) <= ${dueDateEnd}`;
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');

		const allAgentLen = (gdbx.allAgents && gdbx.allAgents.length) || 0;
		const agentFilter = this.selectedAgents && this.selectedAgents.length > 0
			&& this.selectedAgents.length !== allAgentLen
			? ` AND "AGENT" IN (${this.selectedAgents.map((a: string) => `'${escapeStr(a)}'`).join(', ')})`
			: '';

		const allSupplierLen = (gdbx.allSuppliers && gdbx.allSuppliers.length) || 0;
		const supplierFilter = this.selectedSuppliers && this.selectedSuppliers.length > 0
			&& this.selectedSuppliers.length !== allSupplierLen
			? ` AND "CODE" IN (${this.selectedSuppliers.map((s: string) => `'${escapeStr(s)}'`).join(', ')})`
			: '';

		const rows = await queryHead.rawQuery(`
			SELECT
				TRY_CAST("DUEDATE" AS BIGINT) as dueDate,
				"DOCNO" as docNo,
				"TERMS" as terms,
				"CODE" as code,
				"AGENT" as agent,
				TRY_CAST("LOCALDOCOSAMT" AS DOUBLE) as osAmount
			FROM ${piSdsTable}
			WHERE ${dateFilter}${agentFilter}${supplierFilter}
		`);
		this.checkInterrupted(jobId);
		this.generateLoadingText(1);

		const filteredRows = rows.filter((item: any) => (Number(item.osAmount) || 0) !== 0);

		this.tableFields = [
			{
				key: 'due_date',
				sortable: true,
				formatter: TableItemFormatter.date,
			},
			{
				key: 'doc_no',
				sortable: false,
			},
			{
				key: 'term',
				sortable: false,
			},
			{
				key: 'supplier',
				sortable: true,
			},
			{
				key: 'agent',
				sortable: true,
			},
			{
				key: 'outstanding_amount',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
		];

		const result = filteredRows.map((item: any) => {
			const object = {
				due_date: Number(item.dueDate) || 0,
				doc_no: item.docNo,
				term: item.terms,
				supplier: gdbx.supplierNames[item.code],
				agent: gdbx.agentNames[item.agent],
				outstanding_amount: Number(item.osAmount) || 0,
			};

			return object;
		});

		this.tableItems = result;

		this.saveHistory('tableItems', 'tableFields');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
